import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  DatePicker,
  Empty,
  Input,
  Layout,
  Modal,
  Popover,
  Progress,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  BarChartOutlined,
  BulbOutlined,
  CalendarOutlined,
  CloudDownloadOutlined,
  DashboardOutlined,
  FileTextOutlined,
  LineChartOutlined,
  SearchOutlined,
  TeamOutlined,
  UserSwitchOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import apiClient, { analyticsApi, DashboardStats, PaidUsersGrowthData, UserBehavior } from '../services/api';
import { VipStatus } from '../types';
import AnalyticsModeSwitch from './AnalyticsModeSwitch';
import UserSearchModal from './UserSearchModal';
import './DashboardV2.css';

const { Header, Sider, Content } = Layout;
const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

type PaidUser = {
  account_id: string;
  kaiin_code?: number;
  full_name?: string;
  email?: string;
  specialty?: string;
  prefecture?: string;
  clinic_name?: string;
  vip_status?: number;
  subscription_product?: string;
  first_subscription_time?: string;
  created_at?: string;
  // V2 (post 2026-05-26 Stripe sync)
  stripe_customer_id?: string;
  stripe_subscription_id?: string;
  stripe_status?: string;
  stripe_coupon_id?: string;
  coupon_end_date?: string;
  last_invoice_paid?: number;
  total_paid_to_date?: number;
  subscription_source?: 'stripe_paid' | 'stripe_coupon' | 'manual' | 'unknown';
  stripe_verified_at?: string;
  last_login_time?: string;
};

type CancellationRow = {
  account_id: string;
  kaiin_code?: number;
  full_name?: string;
  email?: string;
  stripe_subscription_id?: string;
  stripe_status?: string;
  stripe_canceled_at?: string;
  subscription_source?: string;
  first_subscription_time?: string;
  vip_status?: number;
  source?: 'db' | 'stripe' | 'both' | 'unknown';
};

type FrictionSignal = {
  count: number;
  last_at: string | null;
  sample_html?: string | null;
  sample_url?: string | null;
  sample_page?: string | null;
  sample_hash?: string | null;
  sample_signature?: string | null;
  sample_url_path?: string | null;
  label?: {
    label: string;
    page?: string | null;
    labeled_by?: string | null;
    labeled_at?: string | null;
    notes?: string | null;
    matched_by?: string;
  } | null;
};

const TREAT_TABS: Record<string, string> = {
  '0': '計測', '1': 'ポリゴン', '2': 'DIPモード', '3': '重ね合わせ',
  '4': '評価', '6': 'プロフィログラム', '7': 'PA', '8': 'Occlusogram',
};

const parseTabIndex = (url?: string | null): string | null => {
  if (!url) return null;
  const m = url.match(/[?&]tabindex=(\d+)/);
  if (!m) return null;
  return TREAT_TABS[m[1]] || `tab=${m[1]}`;
};
type FrictionUser = {
  account_id: string;
  kaiin_code?: number;
  full_name?: string;
  email?: string;
  vip_status?: number;
  subscription_source?: string;
  last_login_time?: string;
  signals: Record<string, FrictionSignal>;
  total_signal_count: number;
  dominant_quality?: 'engaged' | 'browsing' | 'tab_open' | null;
  quality_breakdown?: Record<string, number>;
};
type LLMAnalysis = {
  account_id: string;
  days: number;
  event_count: number;
  click_count: number;
  rule_signals: Record<string, FrictionSignal>;
  llm_analysis: {
    summary_timeline?: string;
    friction_points?: Array<{ step: string; issue: string; evidence_time: string }>;
    drop_reason_hypothesis?: string;
    ui_improvement?: string[];
    raw_text?: string;
    _parse_error?: boolean;
  };
  usage?: { input_tokens?: number; output_tokens?: number; cache_creation_input_tokens?: number; cache_read_input_tokens?: number };
  model?: string;
  cached?: boolean;
  analyzed_at?: string;
};

const SIGNAL_LABELS: Record<string, { ja: string; color: string; hint: string }> = {
  repeat_click: { ja: '反復クリック', color: 'red', hint: '同要素を 3 秒で 3 回以上' },
  instant_cancel: { ja: '即時取消', color: 'orange', hint: '確認後 2 秒以内に取消' },
  bounce_page: { ja: 'バウンス', color: 'gold', hint: 'ページ滞在 5 秒未満' },
  repeat_enter: { ja: '入退反復', color: 'gold', hint: '5 分以内に同ページ 3 回以上' },
  abandoned_flow: { ja: '流程放棄', color: 'red', hint: 'funnel 中断' },
  error_then_logout: { ja: 'エラー後離脱', color: 'red', hint: 'システムエラー後 5 分以内に活動消失' },
  idle_on_page: { ja: 'ページ放置', color: 'blue', hint: '同ページ 5 分以上 click なし' },
  form_stuck: { ja: 'フォーム詰まり', color: 'orange', hint: '次へ反復失败' },
  dropdown_thrash: { ja: 'ドロップダウン迷い', color: 'gold', hint: '5 回以上切替' },
  scroll_to_bottom_no_action: { ja: '底スクロール無操作', color: 'blue', hint: 'コンテンツ未発見' },
  no_biz_event: { ja: '業務イベント無', color: 'default', hint: 'session 内に AI/画像/PDF 無し' },
};

type AlertUser = PaidUser & {
  alert_count?: number;
  last_alert_time?: string;
  image_uploaded_count?: number;
};

type FeatureUsage = {
  page: string;
  events: number;
  uniqueUsers: number;
  eventsPerUser: number;
  avgDuration: number;
};

type RiskUser = {
  key: string;
  name: string;
  email: string;
  clinic: string;
  product: string;
  usageRate: number;
  activeDays: number;
  recentActiveDays: number;
  lastUsed: string;
  inactiveDays: number;
  usesMeasurement: boolean;
  topPages: string;
  risk: '高' | '中' | '低';
  reason: string;
};

type ChurnRow = {
  date: string;
  cancellations: number;
  newPaid: number;
  net: number;
  paidUsers: number;
};

type FrameworkRow = {
  key: string;
  module: string;
  current: string;
  blocker: string;
  next: string;
  status: 'ready' | 'partial' | 'blocked';
};

const INTERNAL_STATUSES = new Set<number>([VipStatus.INTERNAL]);
const DEFAULT_DAYS = 30;

const formatNumber = (value?: number) => (value || 0).toLocaleString('ja-JP');

const isBehaviorUser = (user: PaidUser | UserBehavior): user is UserBehavior => 'user_name' in user;

const getName = (user: PaidUser | UserBehavior) => {
  const candidate = isBehaviorUser(user) ? user.user_name : user.full_name;
  return candidate || `User ${('account_id' in user ? user.account_id : user.user_id || '').slice(-8)}`;
};

const getUserKey = (user: PaidUser | UserBehavior) => {
  if ('account_id' in user && user.account_id) return user.account_id;
  if ('user_id' in user && user.user_id) return user.user_id;
  return getName(user);
};

const getProduct = (user?: PaidUser) => user?.subscription_product || '不明';

const VIP_TIER_LABEL: Record<number, { label: string; color: string }> = {
  0: { label: 'Free',     color: 'default' },
  1: { label: 'Pro',      color: 'gold' },
  2: { label: '学会',     color: 'cyan' },
  3: { label: 'Partner',  color: 'purple' },
  4: { label: '運営',     color: 'magenta' },
};

const renderContractSource = (src: string | null | undefined, vipStatus: number | null | undefined) => {
  const vip = VIP_TIER_LABEL[Number(vipStatus ?? -1)];
  const knownSrc = src && src !== 'unknown' ? src : null;
  if (knownSrc) {
    return (
      <div>
        <Tag color={knownSrc === 'stripe_paid' ? 'green' : knownSrc === 'stripe_coupon' ? 'blue' : knownSrc === 'manual' ? 'orange' : 'default'}>{knownSrc}</Tag>
        {vip && <div className="v2-muted" style={{ fontSize: 11 }}>{vip.label}</div>}
      </div>
    );
  }
  return vip ? <Tag color={vip.color}>{vip.label}</Tag> : <Tag>unknown</Tag>;
};

const isInternalAccount = (user: PaidUser | UserBehavior) => {
  const vipStatus = Number(user.vip_status || 0);
  return INTERNAL_STATUSES.has(vipStatus);
};

const isRetiredCandidate = (name?: string) => {
  if (!name) return false;
  return name.includes('退会') || name.includes('削除') || name.toLowerCase().includes('deleted');
};

const toCsv = (rows: Record<string, unknown>[], filename: string) => {
  if (!rows.length) {
    message.info('出力できるデータがありません');
    return;
  }
  const headers = Object.keys(rows[0]);
  const escapeCell = (value: unknown) => {
    const text = value === null || value === undefined ? '' : String(value);
    return `"${text.replace(/"/g, '""')}"`;
  };
  const csv = [headers.join(','), ...rows.map((row) => headers.map((header) => escapeCell(row[header])).join(','))].join('\n');
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(url);
};

const miniBars = (items: Array<{ label: string; value: number }>) => {
  const maxValue = Math.max(...items.map((item) => item.value), 1);
  return (
    <div className="v2-bar-list">
      {items.map((item) => (
        <div className="v2-bar-row" key={item.label}>
          <span>{item.label}</span>
          <div className="v2-bar-track">
            <div className="v2-bar-fill" style={{ width: `${Math.max((item.value / maxValue) * 100, 4)}%` }} />
          </div>
          <strong>{formatNumber(item.value)}</strong>
        </div>
      ))}
    </div>
  );
};

type MetricScope = '現在' | '期間内' | '累計' | '今後30日';

const SCOPE_STYLE: Record<MetricScope, { color: string; bg: string }> = {
  '現在':     { color: '#0369a1', bg: 'rgba(14,165,233,0.12)' },   // 青系 = real-time
  '期間内':   { color: '#9a3412', bg: 'rgba(249,115,22,0.12)' },   // 橙系 = period
  '累計':     { color: '#6b21a8', bg: 'rgba(139,92,246,0.12)' },   // 紫系 = lifetime
  '今後30日': { color: '#92400e', bg: 'rgba(245,158,11,0.14)' },   // 金系 = forward
};

const metricCard = (
  label: string,
  value: string | number,
  hint: string,
  tone: string,
  scope?: MetricScope,
) => (
  <Card className="v2-metric-card">
    {scope && (
      <span className="v2-metric-scope" style={SCOPE_STYLE[scope]}>
        {scope}
      </span>
    )}
    <div className={`v2-metric-dot ${tone}`} />
    <Text className="v2-metric-label">{label}</Text>
    <div className="v2-metric-value">{value}</div>
    <Text className="v2-metric-hint">{hint}</Text>
  </Card>
);

const statusTag = (status: FrameworkRow['status']) => {
  if (status === 'ready') return <Tag color="green">Ready</Tag>;
  if (status === 'partial') return <Tag color="gold">Partial</Tag>;
  return <Tag color="red">Blocked</Tag>;
};

const placeholderPanel = (title: string, description: string, items: string[]) => (
  <Card title={title}>
    <div className="v2-placeholder">
      <Text>{description}</Text>
      <div className="v2-checklist">
        {items.map((item) => (
          <div className="v2-checklist-row" key={item}>
            <span />
            <p>{item}</p>
          </div>
        ))}
      </div>
    </div>
  </Card>
);

const DashboardV2: React.FC = () => {
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([dayjs().subtract(DEFAULT_DAYS - 1, 'day'), dayjs()]);
  const [activeTab, setActiveTab] = useState('overview');
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
  const [paidGrowth, setPaidGrowth] = useState<PaidUsersGrowthData | null>(null);
  const [paidUsers, setPaidUsers] = useState<PaidUser[]>([]);
  const [behaviorsByDate, setBehaviorsByDate] = useState<Record<string, UserBehavior[]>>({});
  const [alertUsers, setAlertUsers] = useState<AlertUser[]>([]);
  const [cancellations, setCancellations] = useState<{
    pending_cancel: CancellationRow[];
    canceled: CancellationRow[];
    payment_failed: CancellationRow[];
    total_by_status?: { pending_cancel: number; canceled: number; payment_failed: number };
  }>({ pending_cancel: [], canceled: [], payment_failed: [] });
  const [lifetimeCancellations, setLifetimeCancellations] = useState<{
    canceled_count: number;
  }>({ canceled_count: 0 });
  // Contract events (spec §6-3)
  type ContractEvent = {
    pay_id: number;
    time: string;
    event_type: 'subscription_started' | 'subscription_renewed' | 'subscription_cancelled' | 'payment_failed';
    account_id: string;
    kaiin_code?: number;
    full_name?: string;
    email?: string;
    subscription_id?: string;
    clinic_name?: string;
    product?: string;
    amount?: number | null;
  };
  const [contractEvents, setContractEvents] = useState<{
    events: ContractEvent[];
    summary?: { subscription_started: number; subscription_renewed: number; subscription_cancelled: number; payment_failed: number; total: number };
  }>({ events: [] });

  // Activation funnel (spec §4-9, §6 / §9 P1)
  const [funnelData, setFunnelData] = useState<{
    funnel: Array<{ step: string; users: number; description?: string; from_register: number | null; from_prev: number | null }>;
    user_count?: number;
    period?: { start: string; end: string };
  } | null>(null);

  // Paid table pagination (controlled so we can jump to a specific user)
  const PAID_PAGE_SIZE = 10;
  const [paidCurrentPage, setPaidCurrentPage] = useState(1);
  const [highlightAccountId, setHighlightAccountId] = useState<string | null>(null);
  const [frictionUsers, setFrictionUsers] = useState<FrictionUser[]>([]);
  const [frictionLoading, setFrictionLoading] = useState(false);
  // task #31: hash 命名 modal
  const [labelModal, setLabelModal] = useState<{
    open: boolean; html_hash?: string; page?: string; sample_url?: string; sample_html?: string;
    signature?: string; url_path?: string;
    label?: string; notes?: string; saving?: boolean; existingLabel?: string;
  }>({ open: false });
  const [llmModal, setLlmModal] = useState<{ visible: boolean; accountId?: string; user?: FrictionUser; loading: boolean; data?: LLMAnalysis; error?: string }>({ visible: false, loading: false });
  // 期間比較 (#28)
  const [periodA, setPeriodA] = useState<[Dayjs, Dayjs]>([dayjs().subtract(13, 'day'), dayjs()]);
  const [periodB, setPeriodB] = useState<[Dayjs, Dayjs]>([dayjs().subtract(27, 'day'), dayjs().subtract(14, 'day')]);
  const [compareSegment, setCompareSegment] = useState<string>('');
  const [compareSegmentValue, setCompareSegmentValue] = useState<string>('');
  const [compareResult, setCompareResult] = useState<{
    comparison: Array<{ key: string; metric: string; periodA: number; periodB: number; delta: number; change_pct: number | null }>;
    segment_options?: { prefecture: string[]; specialty: string[]; subscription_source: string[]; vip_status: number[] };
  } | null>(null);
  const [compareLoading, setCompareLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string>('');
  const [riskFilter, setRiskFilter] = useState<string>('all');
  const [productFilter, setProductFilter] = useState<string>('all');
  const [searchText, setSearchText] = useState('');
  const [searchModalVisible, setSearchModalVisible] = useState(false);

  useEffect(() => {
    const initialize = async () => {
      setLoading(true);
      setLoadError('');
      try {
        const dates = await analyticsApi.getAvailableDates();
        const latest = dates.latest_date ? dayjs(dates.latest_date) : dayjs();
        const nextDates = dates.available_dates || [];
        setAvailableDates(nextDates);
        setDateRange([latest.subtract(DEFAULT_DAYS - 1, 'day'), latest]);
        if (!nextDates.length) {
          setLoadError('利用可能な分析日付がありません。ログ集計が完了しているか確認してください。');
          setLoading(false);
        }
      } catch (error) {
        setLoadError('分析APIに接続できません。バックエンドとDB接続を確認してください。');
        message.error('利用可能日の取得に失敗しました');
        setLoading(false);
      }
    };
    initialize();
  }, []);

  useEffect(() => {
    const load = async () => {
      const start = dateRange[0].format('YYYY-MM-DD');
      const end = dateRange[1].format('YYYY-MM-DD');
      const days = dateRange[1].diff(dateRange[0], 'day') + 1;
      setLoading(true);
      setLoadError('');
      try {
        // 2026-05-26: V2 endpoint 切替 (Bug A 修正 + Stripe sync 反映).
        const [stats, statsV2, growth, proResponseV2, alerts, cancResponse, lifetimeCancResponse] = await Promise.all([
          analyticsApi.getDashboardStats(end),
          apiClient.get(`/api/analytics/v2/dashboard-stats/${end}`) as unknown as Promise<{
            activeUsers: number; paidUsers: number; newUsers: number; totalUsers: number;
            alertUsers: number; couponExpiringIn30Days: number;
            byStripeSource: Record<string, number>;
          }>,
          analyticsApi.getPaidUsersGrowth(days, end),
          apiClient.get('/api/analytics/v2/pro-users') as unknown as Promise<{
            pro_users: PaidUser[]; total_count: number; by_source: Record<string, number>;
          }>,
          apiClient.get('/api/analytics/alert-users') as unknown as Promise<{ alert_users: AlertUser[] }>,
          apiClient.get('/api/analytics/v2/cancellations', {
            params: { start, end },  // 期間内 cancel filter (概要 churn rate 正確化)
          }) as unknown as Promise<{
            pending_cancel: CancellationRow[]; canceled: CancellationRow[];
            payment_failed: CancellationRow[];
            total_by_status: { pending_cancel: number; canceled: number; payment_failed: number };
          }>,
          // Lifetime cancellation count for "累計契約者" metric
          apiClient.get('/api/analytics/v2/cancellations') as unknown as Promise<{
            total_by_status: { pending_cancel: number; canceled: number; payment_failed: number };
          }>,
        ]);

        const datesToLoad = availableDates
          .filter((date) => date >= start && date <= end)
          .slice(-Math.min(days, 45));
        const behaviorEntries = await Promise.all(
          datesToLoad.map(async (date) => {
            try {
              const response = await analyticsApi.getUserBehaviors(date, 1, 500);
              return [date, response.users || []] as const;
            } catch {
              return [date, []] as const;
            }
          })
        );

        // 2026-05-26: V2 dashboard-stats が正しい totalUsers/newUsers (内部・退会除外)。
        // V2 にない field (planDistribution, topPages, last30DaysTrend) は legacy から流用。
        setDashboardStats({ ...stats, ...statsV2 } as DashboardStats);
        setPaidGrowth(growth);
        setPaidUsers((proResponseV2.pro_users || []) as PaidUser[]);
        setAlertUsers(alerts.alert_users || []);
        setCancellations({
          pending_cancel: cancResponse.pending_cancel || [],
          canceled: cancResponse.canceled || [],
          payment_failed: cancResponse.payment_failed || [],
          total_by_status: cancResponse.total_by_status,
        });
        setLifetimeCancellations({
          canceled_count: lifetimeCancResponse.total_by_status?.canceled ?? 0,
        });
        setBehaviorsByDate(Object.fromEntries(behaviorEntries));

        // Contract events (期間内)
        try {
          const ce = await (apiClient.get('/api/analytics/v2/contract-events', {
            params: { start, end },
          }) as unknown as Promise<any>);
          setContractEvents(ce);
        } catch (e) { /* silent */ }

        // Activation funnel (lifetime from 2025-04-08 launch to selected end date)
        try {
          const fn = await (apiClient.get('/api/analytics/v2/activation-funnel', {
            params: { start: '2025-04-08', end },
          }) as unknown as Promise<any>);
          setFunnelData(fn);
        } catch (e) { /* silent */ }

        // Friction users 別取得 (失敗しても他の tab は動く)
        setFrictionLoading(true);
        try {
          const fr = await (apiClient.get('/api/analytics/v2/user-friction', {
            params: { days: Math.min(days, 30), limit: 50 },
          }) as unknown as Promise<{ users: FrictionUser[] }>);
          setFrictionUsers(fr.users || []);
        } catch (e) {
          // silent
        } finally {
          setFrictionLoading(false);
        }
      } catch (error) {
        setLoadError('分析データの取得に失敗しました。APIレスポンスまたはDB接続を確認してください。');
        message.error('分析データの取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };

    if (availableDates.length) {
      load();
    }
  }, [availableDates, dateRange]);

  const behaviorRows = useMemo(() => Object.values(behaviorsByDate).flat(), [behaviorsByDate]);

  // 2026-05-26: bug fix — effective / excluded を排他に。
  // excluded 優先: internal OR retired OR (subscription_product 不明)。
  // effective = paidUsers - excluded、二重計算を防ぐ。
  const excludedPaidUsers = useMemo(
    () => paidUsers.filter((user) => isInternalAccount(user) || isRetiredCandidate(user.full_name) || !user.subscription_product),
    [paidUsers]
  );

  const effectivePaidUsers = useMemo(() => {
    const excludedKeys = new Set(excludedPaidUsers.map(getUserKey));
    return paidUsers.filter((user) => !excludedKeys.has(getUserKey(user)));
  }, [paidUsers, excludedPaidUsers]);

  const growthRows = useMemo<ChurnRow[]>(() => {
    const growthData = paidGrowth?.growthData || [];
    return growthData.map((row, index) => {
      const previous = index > 0 ? growthData[index - 1].cumulativePaidUsers : row.cumulativePaidUsers - row.newPaidUsers;
      const delta = row.cumulativePaidUsers - previous;
      const cancellations = Math.max(0, row.newPaidUsers - delta);
      return {
        date: row.date,
        cancellations,
        newPaid: row.newPaidUsers || 0,
        net: delta,
        paidUsers: row.cumulativePaidUsers || 0,
      };
    });
  }, [paidGrowth]);

  // 2026-05-26: cancellations を /v2/cancellations の真実 (期間 filter 付) ベースに切替。
  // 旧 growthRows.cancellations は first_subscription_time 推定で永久に 0 だった。
  const totalCancellations = cancellations.total_by_status?.canceled ?? 0;
  const totalNewPaid = paidGrowth?.total_new_paid_users || growthRows.reduce((sum, row) => sum + row.newPaid, 0);
  const periodStartPaid = growthRows.length ? Math.max(growthRows[0].paidUsers - growthRows[0].net, 0) : 0;
  const churnRate = periodStartPaid ? `${((totalCancellations / periodStartPaid) * 100).toFixed(1)}%` : '0.0%';

  const productCounts = useMemo(() => {
    const counts = effectivePaidUsers.reduce<Record<string, number>>((acc, user) => {
      const product = getProduct(user);
      acc[product] = (acc[product] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(counts).map(([label, value]) => ({ label, value }));
  }, [effectivePaidUsers]);

  const featureUsage = useMemo<FeatureUsage[]>(() => {
    const usage = new Map<string, { events: number; users: Set<string>; duration: number }>();
    behaviorRows.forEach((user) => {
      Object.entries(user.pages_visited || {}).forEach(([page, count]) => {
        const current = usage.get(page) || { events: 0, users: new Set<string>(), duration: 0 };
        current.events += Number(count || 0);
        current.users.add(getUserKey(user));
        current.duration += Number(user.total_duration || 0);
        usage.set(page, current);
      });
    });
    return Array.from(usage.entries())
      .map(([page, value]) => ({
        page,
        events: value.events,
        uniqueUsers: value.users.size,
        eventsPerUser: value.users.size ? Number((value.events / value.users.size).toFixed(1)) : 0,
        avgDuration: value.users.size ? Math.round(value.duration / value.users.size) : 0,
      }))
      .sort((a, b) => b.events - a.events);
  }, [behaviorRows]);

  const riskUsers = useMemo<RiskUser[]>(() => {
    const start = dateRange[0];
    const end = dateRange[1];
    const periodDays = end.diff(start, 'day') + 1;
    const recentStart = end.subtract(29, 'day');
    const activityByUser = new Map<string, { paid?: PaidUser; dates: Set<string>; pages: Record<string, number>; lastUsed: string }>();

    effectivePaidUsers.forEach((user) => {
      activityByUser.set(getUserKey(user), { paid: user, dates: new Set<string>(), pages: {}, lastUsed: '' });
    });

    Object.entries(behaviorsByDate).forEach(([date, users]) => {
      users.forEach((behavior) => {
        const key = getUserKey(behavior);
        const current = activityByUser.get(key) || {
          paid: effectivePaidUsers.find((user) => getName(user) === behavior.user_name),
          dates: new Set<string>(),
          pages: {},
          lastUsed: '',
        };
        current.dates.add(date);
        current.lastUsed = !current.lastUsed || date > current.lastUsed ? date : current.lastUsed;
        Object.entries(behavior.pages_visited || {}).forEach(([page, count]) => {
          current.pages[page] = (current.pages[page] || 0) + Number(count || 0);
        });
        activityByUser.set(key, current);
      });
    });

    return Array.from(activityByUser.entries()).map(([key, value]) => {
      const activeDays = value.dates.size;
      const recentActiveDays = Array.from(value.dates).filter((date) => dayjs(date).isAfter(recentStart.subtract(1, 'day'))).length;
      const usageRate = periodDays ? Math.round((activeDays / periodDays) * 100) : 0;
      const lastUsed = value.lastUsed || '';
      const inactiveDays = lastUsed ? end.diff(dayjs(lastUsed), 'day') : periodDays;
      const topPages = Object.entries(value.pages).sort((a, b) => b[1] - a[1]).slice(0, 3);
      const usesMeasurement = topPages.some(([page]) => page.includes('計測') || page.toLowerCase().includes('measure'));
      const risk: RiskUser['risk'] = inactiveDays >= 30 || activeDays === 0 ? '高' : usageRate < 20 || recentActiveDays <= 1 ? '中' : '低';
      const reason = risk === '高' ? '30日以上未利用または期間内活動なし' : risk === '中' ? '利用率が低い、または直近利用が少ない' : '期間内に継続利用あり';

      return {
        key,
        name: getName(value.paid || ({ user_name: key } as UserBehavior)),
        email: value.paid?.email || '',
        clinic: value.paid?.clinic_name || '',
        product: getProduct(value.paid),
        usageRate,
        activeDays,
        recentActiveDays,
        lastUsed: lastUsed || '未検出',
        inactiveDays,
        usesMeasurement,
        topPages: topPages.map(([page, count]) => `${page}(${count})`).join(' / ') || '-',
        risk,
        reason,
      };
    });
  }, [behaviorsByDate, dateRange, effectivePaidUsers]);

  const filteredRiskUsers = riskUsers.filter((user) => {
    const matchesRisk = riskFilter === 'all' || user.risk === riskFilter;
    const matchesProduct = productFilter === 'all' || user.product === productFilter;
    const search = searchText.trim().toLowerCase();
    const matchesSearch = !search || [user.name, user.email, user.clinic].join(' ').toLowerCase().includes(search);
    return matchesRisk && matchesProduct && matchesSearch;
  });

  const retiredCandidates = behaviorRows
    .filter((user) => isRetiredCandidate(user.user_name))
    .map((user) => ({
      key: getUserKey(user),
      name: user.user_name,
      email: user.email || '',
      clinic: user.clinic || '',
      specialty: user.specialty || '',
      lastActivity: user.last_activity,
      activityCount: user.activity_count,
      pages: Object.keys(user.pages_visited || {}).slice(0, 4).join(' / '),
    }));

  const jumpToPaidUser = (accountId: string) => {
    const idx = effectivePaidUsers.findIndex((u) => u.account_id === accountId);
    if (idx >= 0) {
      const page = Math.floor(idx / PAID_PAGE_SIZE) + 1;
      setPaidCurrentPage(page);
    }
    setHighlightAccountId(accountId);
    setActiveTab('paid');
    // tab + page render 完了後にスクロール (account_id は '|' を含むので CSS.escape)
    setTimeout(() => {
      try {
        const row = document.querySelector(`tr[data-row-key="${(window as any).CSS?.escape ? (window as any).CSS.escape(accountId) : accountId}"]`);
        if (row && row.scrollIntoView) {
          row.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      } catch (e) {
        // ignore selector errors
      }
    }, 300);
    setTimeout(() => setHighlightAccountId(null), 4000);
  };

  const runCompare = async () => {
    setCompareLoading(true);
    try {
      const params: Record<string, string> = {
        periodA_start: periodA[0].format('YYYY-MM-DD'),
        periodA_end: periodA[1].format('YYYY-MM-DD'),
        periodB_start: periodB[0].format('YYYY-MM-DD'),
        periodB_end: periodB[1].format('YYYY-MM-DD'),
      };
      if (compareSegment && compareSegmentValue) {
        params.segment = compareSegment;
        params.segment_value = compareSegmentValue;
      }
      const data = await (apiClient.get('/api/analytics/v2/compare', { params }) as unknown as Promise<any>);
      setCompareResult(data);
    } catch (e: any) {
      message.error(e?.response?.data?.error || e?.message || '比較失敗');
    } finally {
      setCompareLoading(false);
    }
  };

  const runLLMAnalysis = async (user: FrictionUser, force = false) => {
    setLlmModal({ visible: true, accountId: user.account_id, user, loading: true });
    try {
      const data = await (apiClient.post('/api/analytics/v2/user-friction/llm', {
        account_id: user.account_id,
        days: 30,
        force,
      }, { timeout: 120000 }) as unknown as Promise<LLMAnalysis>);
      setLlmModal((prev) => ({ ...prev, loading: false, data }));
    } catch (e: any) {
      const errMsg = e?.response?.data?.error || e?.message || 'LLM 分析失敗';
      const hint = e?.response?.data?.hint;
      setLlmModal((prev) => ({ ...prev, loading: false, error: hint ? `${errMsg}\n${hint}` : errMsg }));
    }
  };

  // task #31: hash 命名 modal helpers
  const openLabelModal = (sig: FrictionSignal) => {
    if (!sig.sample_hash) return;
    setLabelModal({
      open: true,
      html_hash: sig.sample_hash,
      page: sig.sample_page || undefined,
      sample_url: sig.sample_url || undefined,
      sample_html: sig.sample_html || undefined,
      signature: sig.sample_signature || undefined,
      url_path: sig.sample_url_path || undefined,
      label: sig.label?.label || '',
      notes: sig.label?.notes || '',
      existingLabel: sig.label?.label,
    });
  };

  const saveLabel = async () => {
    if (!labelModal.html_hash || !labelModal.label?.trim()) {
      message.warning('label を入力してください');
      return;
    }
    setLabelModal((prev) => ({ ...prev, saving: true }));
    try {
      await apiClient.post('/api/analytics/v2/friction/label-hash', {
        html_hash: labelModal.html_hash,
        element_signature: labelModal.signature,
        sample_html: labelModal.sample_html,
        label: labelModal.label.trim(),
        page: labelModal.page,
        sample_url: labelModal.sample_url,
        notes: labelModal.notes,
      });
      // 楽観的に local frictionUsers 更新
      setFrictionUsers((prev) =>
        prev.map((u) => ({
          ...u,
          signals: Object.fromEntries(
            Object.entries(u.signals).map(([k, sig]) => [
              k,
              sig.sample_hash === labelModal.html_hash
                ? { ...sig, label: { label: labelModal.label!.trim(), labeled_by: 'me', labeled_at: new Date().toISOString(), notes: labelModal.notes || null } }
                : sig,
            ])
          ),
        }))
      );
      message.success('命名を保存しました');
      setLabelModal({ open: false });
    } catch (e: any) {
      message.error(`保存失敗: ${e?.response?.data?.error || e?.message || 'unknown'}`);
      setLabelModal((prev) => ({ ...prev, saving: false }));
    }
  };

  const sourceColor = (src?: string) =>
    src === 'stripe_paid' ? 'green' : src === 'stripe_coupon' ? 'gold' : src === 'manual' ? 'red' : 'default';

  const statusColor = (st?: string) =>
    st === 'active' ? 'green' : st === 'trialing' ? 'blue' : st === 'past_due' || st === 'unpaid' ? 'orange' : st === 'canceled' || st === 'incomplete_expired' ? 'red' : 'default';

  const commonUserColumns: ColumnsType<PaidUser> = [
    {
      title: '氏名 / Email',
      dataIndex: 'full_name',
      width: 220,
      fixed: 'left',
      render: (_, record) => (
        <div>
          <strong>{getName(record)}</strong>
          <div className="v2-muted">{record.email || '-'}</div>
        </div>
      ),
    },
    { title: '医院', dataIndex: 'clinic_name', width: 180, ellipsis: true },
    { title: '地域', dataIndex: 'prefecture', width: 100 },
    { title: '専門', dataIndex: 'specialty', width: 120 },
    {
      title: '契約',
      dataIndex: 'subscription_product',
      width: 100,
      render: (value) => <Tag>{value || '不明'}</Tag>,
    },
    {
      title: '契約源',
      dataIndex: 'subscription_source',
      width: 110,
      render: (v, rec) => {
        const tag = <Tag color={sourceColor(v)}>{v || 'unknown'}</Tag>;
        if (v === 'stripe_coupon' && rec.coupon_end_date) {
          return (
            <Tooltip title={`Coupon 失効: ${rec.coupon_end_date}`}>
              {tag}
            </Tooltip>
          );
        }
        return tag;
      },
      filters: [
        { text: 'stripe_paid', value: 'stripe_paid' },
        { text: 'stripe_coupon', value: 'stripe_coupon' },
        { text: 'manual', value: 'manual' },
        { text: 'unknown', value: 'unknown' },
      ],
      onFilter: (val, rec) => (rec.subscription_source || 'unknown') === val,
    },
    {
      title: 'Stripe Status',
      dataIndex: 'stripe_status',
      width: 110,
      render: (v) => v ? <Tag color={statusColor(v)}>{v}</Tag> : <span className="v2-muted">-</span>,
    },
    { title: '初回課金日', dataIndex: 'first_subscription_time', width: 120 },
    { title: '最終ログイン', dataIndex: 'last_login_time', width: 140, render: (v) => v ? String(v).split(' ')[0] : <span className="v2-muted">-</span> },
  ];

  const riskColumns: ColumnsType<RiskUser> = [
    {
      title: 'Risk',
      dataIndex: 'risk',
      width: 80,
      fixed: 'left',
      render: (risk) => <Tag color={risk === '高' ? 'red' : risk === '中' ? 'gold' : 'green'}>{risk}</Tag>,
    },
    { title: '氏名', dataIndex: 'name', width: 160, fixed: 'left' },
    { title: 'Email', dataIndex: 'email', width: 220 },
    { title: '医院', dataIndex: 'clinic', width: 180 },
    { title: '契約', dataIndex: 'product', width: 100 },
    {
      title: '利用率',
      dataIndex: 'usageRate',
      width: 140,
      render: (value) => <Progress percent={value} size="small" strokeColor={value < 20 ? '#ef4444' : 'var(--v2-blue, #5b9bea)'} />,
    },
    { title: '期間内利用日数', dataIndex: 'activeDays', width: 130 },
    { title: '30日利用日数', dataIndex: 'recentActiveDays', width: 120 },
    { title: '最終利用日', dataIndex: 'lastUsed', width: 120 },
    { title: '未利用日数', dataIndex: 'inactiveDays', width: 110 },
    {
      title: '計測',
      dataIndex: 'usesMeasurement',
      width: 80,
      render: (value) => <Tag color={value ? 'blue' : 'default'}>{value ? 'あり' : 'なし'}</Tag>,
    },
    { title: '主なページ', dataIndex: 'topPages', width: 260 },
    { title: '理由', dataIndex: 'reason', width: 260 },
  ];

  const featureColumns: ColumnsType<FeatureUsage> = [
    { title: 'Page / Feature', dataIndex: 'page', width: 220 },
    { title: 'Events', dataIndex: 'events', width: 100, sorter: (a, b) => a.events - b.events },
    { title: 'Unique users', dataIndex: 'uniqueUsers', width: 120 },
    { title: 'Events / user', dataIndex: 'eventsPerUser', width: 120 },
    { title: 'Avg duration', dataIndex: 'avgDuration', width: 120, render: (value) => `${Math.round(value / 60)} min` },
  ];

  const churnColumns: ColumnsType<ChurnRow> = [
    { title: 'Date', dataIndex: 'date' },
    { title: '解約人数', dataIndex: 'cancellations' },
    { title: '新規有料', dataIndex: 'newPaid' },
    { title: '純増', dataIndex: 'net' },
    { title: '有料会員数', dataIndex: 'paidUsers' },
  ];

  const frameworkRows: FrameworkRow[] = [
    {
      key: 'baseline',
      module: '基本ダッシュボード',
      current: '既存KPIは基本ダッシュボードに残し、運営インサイトは継続・離脱分析として併設',
      blocker: 'なし',
      next: '基本KPIとの差分説明を追加',
      status: 'ready',
    },
    {
      key: 'contract',
      module: '契約イベント',
      current: '累積有料数の差分で解約を推定',
      blocker: 'subscription events / contract_status が未公開',
      next: '契約イベントAPIを追加後に実測へ切替',
      status: 'blocked',
    },
    {
      key: 'identity',
      module: 'ユーザー突合',
      current: 'account_id 優先、欠損時は氏名で補助突合',
      blocker: 'pro-users と user-behaviors の共通 user_id が不安定',
      next: 'APIレスポンスに安定 user_id を追加',
      status: 'partial',
    },
    {
      key: 'internal',
      module: '運営アカウント分離',
      current: 'vip_status と退会文字列で暫定除外',
      blocker: 'role / is_internal / is_test_account が未整備',
      next: '専用フラグをDB/APIに追加',
      status: 'partial',
    },
    {
      key: 'onboarding',
      module: 'PRO活性化ファネル',
      current: '新規有料推移のみ表示',
      blocker: 'first_patient / first_image / first_measurement が未計測',
      next: '活性化イベントをログまたは集計APIに追加',
      status: 'blocked',
    },
    {
      key: 'compare',
      module: '任意期間比較',
      current: '単一期間の集計のみ',
      blocker: '比較UIと履歴スナップショットが未接続',
      next: '期間A/Bの差分テーブルを実装',
      status: 'partial',
    },
  ];

  const frameworkColumns: ColumnsType<FrameworkRow> = [
    { title: 'Module', dataIndex: 'module', width: 150, fixed: 'left' },
    { title: 'Current implementation', dataIndex: 'current', width: 280 },
    { title: 'Blocker', dataIndex: 'blocker', width: 260 },
    { title: 'Next action', dataIndex: 'next', width: 260 },
    { title: 'Status', dataIndex: 'status', width: 100, render: statusTag },
  ];

  const tabItems = [
    {
      key: 'overview',
      label: '概要',
      children: (
        <div className="v2-tab-grid">
          <div className="v2-metric-grid">
            {metricCard('現役有料', effectivePaidUsers.length, '現在 PRO 契約中', 'green', '現在')}
            {metricCard('新規有料', totalNewPaid, '新規契約数', 'blue', '期間内')}
            {metricCard('解約人数', totalCancellations, '解約数 (Stripe + DB)', 'red', '期間内')}
            {metricCard('有料純増', totalNewPaid - totalCancellations, `Churn ${churnRate}`, 'black', '期間内')}
            {/* 2026-05-26: 上限アラート metric 削除。新規/既存全員 PRO 必須移行後、
                直近 30 日で alert は 4 名のみ (3 FREE 旧残 + 1 PRO 旧 10GB 制限当時)、指標として価値喪失。
                CSV エクスポートは csv tab に残す (歴史 audit 用)。*/}
          </div>
          <div className="v2-two-column">
            <Card title="有料会員の増減" extra={<Button size="small" onClick={() => toCsv(growthRows as unknown as Record<string, unknown>[], 'paid_growth.csv')}>CSV</Button>}>
              {miniBars(growthRows.slice(-12).map((row) => ({ label: dayjs(row.date).format('MM/DD'), value: row.paidUsers })))}
            </Card>
            <Card title="よく使われているページ" extra={<Tag>{featureUsage.length} features</Tag>}>
              {miniBars(featureUsage.slice(0, 8).map((item) => ({ label: item.page, value: item.events })))}
            </Card>
          </div>
          {/* 2026-05-26: 旧 banner 削除。banner は V2 初版 (推定値依存) の免責だが、
              現在は Stripe sync + 全員 sync + stripe_customer_id 突合で全て真値ベース。
              banner の説明と現実装が乖離していたため削除。 */}
        </div>
      ),
    },
    {
      key: 'paid',
      label: '有料会員',
      children: (
        <div className="v2-tab-grid">
          <div className="v2-metric-grid">
            {(() => {
              const active = effectivePaidUsers.length;
              const lifetimeCanceled = lifetimeCancellations.canceled_count;
              const lifetime = active + lifetimeCanceled;
              const retentionPct = lifetime ? ((active / lifetime) * 100).toFixed(1) : '0.0';
              const manualUsers = paidUsers.filter((u) => u.subscription_source === 'manual');
              const manualCount = manualUsers.length;
              const today = dayjs();
              const thirtyDaysOut = today.add(30, 'day');
              const couponExpiringUsers = paidUsers.filter((u) => {
                if (!u.coupon_end_date) return false;
                const d = dayjs(u.coupon_end_date);
                return d.isValid() && !d.isBefore(today, 'day') && !d.isAfter(thirtyDaysOut, 'day');
              }).sort((a, b) => (a.coupon_end_date! < b.coupon_end_date! ? -1 : 1));
              const couponExpiring30 = couponExpiringUsers.length;

              const couponPopoverContent = couponExpiringUsers.length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="該当なし" />
              ) : (
                <div style={{ minWidth: 320, maxHeight: 320, overflowY: 'auto' }}>
                  {couponExpiringUsers.map((u) => {
                    const days = u.coupon_end_date ? dayjs(u.coupon_end_date).diff(today, 'day') : null;
                    return (
                      <div
                        key={u.account_id}
                        onClick={() => jumpToPaidUser(u.account_id)}
                        style={{ padding: '8px 4px', borderBottom: '1px solid var(--v2-border-soft)', cursor: 'pointer' }}
                      >
                        <div><strong>{u.full_name || '(無名)'}</strong></div>
                        <div className="v2-muted" style={{ fontSize: 12 }}>{u.email || '-'}</div>
                        <div className="v2-muted" style={{ fontSize: 12 }}>{u.clinic_name || '-'}</div>
                        <div style={{ fontSize: 12, marginTop: 4 }}>
                          <Tag color={days != null && days <= 7 ? 'red' : 'gold'}>
                            {u.coupon_end_date} (あと {days} 日)
                          </Tag>
                          <span className="v2-muted">クリックで一覧へ</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );

              const manualPopoverContent = manualUsers.length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="該当なし (健全)" />
              ) : (
                <div style={{ minWidth: 320, maxHeight: 320, overflowY: 'auto' }}>
                  {manualUsers.map((u) => (
                    <div
                      key={u.account_id}
                      onClick={() => jumpToPaidUser(u.account_id)}
                      style={{ padding: '8px 4px', borderBottom: '1px solid var(--v2-border-soft)', cursor: 'pointer' }}
                    >
                      <div><strong>{u.full_name || '(無名)'}</strong></div>
                      <div className="v2-muted" style={{ fontSize: 12 }}>{u.email || '-'}</div>
                      <div className="v2-muted" style={{ fontSize: 12 }}>{u.clinic_name || '-'}</div>
                      <div style={{ fontSize: 12, marginTop: 4 }}>
                        <Tag color="red">Stripe sub 無し</Tag>
                        <span className="v2-muted">クリックで一覧へ</span>
                      </div>
                    </div>
                  ))}
                </div>
              );

              return (
                <>
                  {metricCard('現役有料', active, `含 手動付与 ${manualCount}`, 'green', '現在')}
                  {metricCard('累計契約者', lifetime, `現役 ${active} + 解約済 ${lifetimeCanceled}`, 'purple', '累計')}
                  {metricCard('解約済 (歴史)', lifetimeCanceled, '今までに解約した PRO', 'red', '累計')}
                  {metricCard('保留率', `${retentionPct}%`, '現役 / 累計', 'blue', '累計')}
                  <Popover content={manualPopoverContent} title="手動付与 ユーザ (Stripe sub 無し)" placement="bottom" trigger="hover">
                    <div style={{ cursor: manualCount > 0 ? 'pointer' : 'default' }}>
                      {metricCard('手動付与', manualCount, '異常監視 (通常 0)', manualCount > 0 ? 'red' : 'default', '現在')}
                    </div>
                  </Popover>
                  <Popover content={couponPopoverContent} title="近日 Coupon 失効ユーザ" placement="bottom" trigger="hover">
                    <div style={{ cursor: couponExpiring30 > 0 ? 'pointer' : 'default' }}>
                      {metricCard('Coupon 失効 30日内', couponExpiring30, '近日 Coupon 切れで料金発生', couponExpiring30 > 0 ? 'gold' : 'default', '今後30日')}
                    </div>
                  </Popover>
                  {/* 7th card: 月額/年額 stacked bar + 数 + % */}
                  {(() => {
                    const total = productCounts.reduce((s, p) => s + p.value, 0);
                    const colors: Record<string, string> = {
                      '月額': '#3b82f6',
                      '年額': '#10b981',
                      'ストレージ': '#f59e0b',
                      '不明': '#9ca3af',
                    };
                    return (
                      <Card className="v2-metric-card">
                        <div className="v2-metric-dot cyan" />
                        <Text className="v2-metric-label">月額 / 年額</Text>
                        <div style={{ display: 'flex', height: 10, borderRadius: 4, overflow: 'hidden', marginTop: 4 }}>
                          {productCounts.map((p) => (
                            <div
                              key={p.label}
                              title={`${p.label} ${p.value} (${total ? ((p.value / total) * 100).toFixed(1) : '0'}%)`}
                              style={{
                                width: `${total ? (p.value / total) * 100 : 0}%`,
                                background: colors[p.label] || '#777',
                              }}
                            />
                          ))}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 4, fontSize: 11 }}>
                          {productCounts.map((p) => {
                            const pct = total ? ((p.value / total) * 100).toFixed(1) : '0';
                            return (
                              <div key={p.label} style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>
                                  <span style={{ display: 'inline-block', width: 8, height: 8, background: colors[p.label] || '#777', marginRight: 4, borderRadius: 2 }} />
                                  {p.label}
                                </span>
                                <span><strong>{p.value}</strong> ({pct}%)</span>
                              </div>
                            );
                          })}
                        </div>
                      </Card>
                    );
                  })()}
                </>
              );
            })()}
          </div>
          <Card title="有料会員一覧" extra={<Button size="small" icon={<CloudDownloadOutlined />} onClick={() => toCsv(effectivePaidUsers as Record<string, unknown>[], 'paid_users.csv')}>CSV</Button>}>
            <Table
              size="small"
              rowKey={getUserKey}
              columns={commonUserColumns}
              dataSource={effectivePaidUsers}
              pagination={{
                pageSize: PAID_PAGE_SIZE,
                current: paidCurrentPage,
                onChange: setPaidCurrentPage,
                showSizeChanger: false,
              }}
              scroll={{ x: 900 }}
              rowClassName={(rec) => rec.account_id === highlightAccountId ? 'v2-row-highlight' : ''}
            />
          </Card>
        </div>
      ),
    },
    {
      key: 'churn',
      label: '解約・退会',
      children: (
        <div className="v2-tab-grid">
          <div className="v2-metric-grid">
            {metricCard('即将解約 (pending)', cancellations.total_by_status?.pending_cancel ?? cancellations.pending_cancel.length, 'Stripe 取消申請済 (期末停止)', 'gold', '現在')}
            {metricCard('已解約', cancellations.total_by_status?.canceled ?? cancellations.canceled.length, '解約 (Stripe + DB 並集)', 'red', '期間内')}
            {metricCard('支払い停止', cancellations.total_by_status?.payment_failed ?? cancellations.payment_failed.length, 'past_due / unpaid', 'red', '現在')}
            {metricCard('退会済み', retiredCandidates.length, 'ログ上の退会候補 (別軸)', 'blue', '期間内')}
          </div>
          <Card title="即将解約 (pending cancel)" extra={<Button size="small" onClick={() => toCsv(cancellations.pending_cancel as unknown as Record<string, unknown>[], 'pending_cancel.csv')}>CSV</Button>}>
            <Table size="small" rowKey="account_id" dataSource={cancellations.pending_cancel} pagination={{ pageSize: 10 }}
              columns={[
                { title: 'kaiin', dataIndex: 'kaiin_code', width: 80 },
                { title: '氏名', dataIndex: 'full_name', width: 160 },
                { title: 'email', dataIndex: 'email', width: 200, ellipsis: true },
                { title: 'Stripe Status', dataIndex: 'stripe_status', width: 110, render: (v: string) => <Tag color={statusColor(v)}>{v}</Tag> },
                { title: 'Canceled at', dataIndex: 'stripe_canceled_at', width: 140 },
                { title: '契約源', dataIndex: 'subscription_source', width: 110, render: (v: string) => <Tag color={sourceColor(v)}>{v || 'unknown'}</Tag> },
              ]} scroll={{ x: 1000 }} />
          </Card>
          <Card title="已解約 (canceled, 並集)" extra={<Button size="small" onClick={() => toCsv(cancellations.canceled as unknown as Record<string, unknown>[], 'canceled.csv')}>CSV</Button>}>
            <Table size="small" rowKey="account_id" dataSource={cancellations.canceled} pagination={{ pageSize: 10 }}
              columns={[
                { title: 'kaiin', dataIndex: 'kaiin_code', width: 80 },
                { title: '氏名', dataIndex: 'full_name', width: 160 },
                { title: 'email', dataIndex: 'email', width: 200, ellipsis: true },
                {
                  title: 'Source',
                  dataIndex: 'source',
                  width: 90,
                  render: (v: string) => <Tag color={v === 'both' ? 'green' : v === 'stripe' ? 'blue' : v === 'db' ? 'orange' : 'default'}>{v}</Tag>,
                  filters: [
                    { text: 'both', value: 'both' },
                    { text: 'stripe', value: 'stripe' },
                    { text: 'db', value: 'db' },
                  ],
                  onFilter: (val, rec) => rec.source === val,
                },
                { title: 'Stripe Status', dataIndex: 'stripe_status', width: 130, render: (v: string) => v ? <Tag color={statusColor(v)}>{v}</Tag> : '-' },
                { title: 'Canceled at', dataIndex: 'stripe_canceled_at', width: 140 },
                { title: '初回課金日', dataIndex: 'first_subscription_time', width: 140 },
              ]} scroll={{ x: 1100 }} />
          </Card>
          <Card title="ユーザーログ上の退会済み候補 (旧表示維持)" extra={<Button size="small" onClick={() => toCsv(retiredCandidates, 'retired_log_candidates.csv')}>CSV</Button>}>
            <Table size="small" rowKey="key" dataSource={retiredCandidates} pagination={{ pageSize: 8 }} columns={[
              { title: 'Name', dataIndex: 'name' },
              { title: 'Email', dataIndex: 'email' },
              { title: 'Clinic', dataIndex: 'clinic' },
              { title: 'Specialty', dataIndex: 'specialty' },
              { title: 'Last activity', dataIndex: 'lastActivity' },
              { title: 'Activities', dataIndex: 'activityCount' },
              { title: 'Pages', dataIndex: 'pages' },
            ]} scroll={{ x: 1000 }} />
          </Card>
        </div>
      ),
    },
    {
      key: 'pages',
      label: '利用ページ',
      children: (
        <Card title="ページ / 機能別利用頻度" extra={<Button size="small" onClick={() => toCsv(featureUsage as unknown as Record<string, unknown>[], 'feature_usage.csv')}>CSV</Button>}>
          <Table size="small" rowKey="page" columns={featureColumns} dataSource={featureUsage} pagination={{ pageSize: 12 }} />
        </Card>
      ),
    },
    {
      key: 'dormant',
      label: '休眠候補',
      children: (
        <Card title="At-risk paid users" extra={<Button size="small" onClick={() => toCsv(riskUsers.filter((user) => user.risk !== '低') as unknown as Record<string, unknown>[], 'dormant_paid_users.csv')}>CSV</Button>}>
          <Table size="small" rowKey="key" columns={riskColumns} dataSource={riskUsers.filter((user) => user.risk !== '低')} pagination={{ pageSize: 10 }} scroll={{ x: 1800 }} />
        </Card>
      ),
    },
    {
      key: 'risk',
      label: 'リスクランキング',
      children: (
        <div className="v2-tab-grid">
          <Card>
            <Space wrap>
              <Input prefix={<SearchOutlined />} placeholder="氏名 / Email / 医院" value={searchText} onChange={(event) => setSearchText(event.target.value)} style={{ width: 260 }} />
              <Select value={riskFilter} onChange={setRiskFilter} options={[{ value: 'all', label: 'All risks' }, { value: '高', label: '高' }, { value: '中', label: '中' }, { value: '低', label: '低' }]} style={{ width: 140 }} />
              <Select value={productFilter} onChange={setProductFilter} options={[{ value: 'all', label: 'All products' }, ...productCounts.map((item) => ({ value: item.label, label: item.label }))]} style={{ width: 160 }} />
              <Button onClick={() => toCsv(filteredRiskUsers as unknown as Record<string, unknown>[], 'risk_ranking.csv')}>CSV</Button>
            </Space>
          </Card>
          <Table size="small" rowKey="key" columns={riskColumns} dataSource={filteredRiskUsers} pagination={{ pageSize: 12 }} scroll={{ x: 1800 }} />
        </div>
      ),
    },
    {
      key: 'onboard',
      label: 'オンボード',
      children: (
        <div className="v2-tab-grid">
          <div className="v2-metric-grid">
            {metricCard('新規有料合計', totalNewPaid, '新規 PRO 契約数', 'green', '期間内')}
            {metricCard('1日平均 新規有料', dateRange[1].diff(dateRange[0], 'day') >= 0 ? (totalNewPaid / (dateRange[1].diff(dateRange[0], 'day') + 1)).toFixed(1) : '0', '新規有料 / 日数', 'purple', '期間内')}
            {metricCard('Best day (最大日次新規)', growthRows.reduce((m, r) => Math.max(m, r.newPaid), 0), '期間内最大日次新規', 'gold', '期間内')}
            {metricCard('総ユーザー', dashboardStats?.totalUsers || 0, '内部・運営除く全員', 'blue', '現在')}
          </div>
          <Card
            title="活性化ファネル (新規登録 → 有料転換)"
            extra={funnelData && (
              <Space size="small">
                <Tag color="#6b21a8" style={{ background: 'rgba(139,92,246,0.12)', borderColor: 'transparent' }}>累計</Tag>
                <span className="v2-muted" style={{ fontSize: 12 }}>
                  cohort: 全ユーザ {funnelData.user_count} 名 (退会含、内部・運営除外)
                </span>
              </Space>
            )}
          >
            {!funnelData || !funnelData.funnel?.length ? (
              <Empty description="データなし" />
            ) : (
              (() => {
                const stepLabels: Record<string, string> = {
                  register: '① 新規登録',
                  first_login: '② 初回ログイン',
                  first_image_upload: '③ 画像アップロード',
                  first_ai_measurement: '④ AI 計測実行',
                  paid_conversion: '⑤ 有料転換',
                };
                const top = funnelData.funnel[0].users || 1;
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {funnelData.funnel.map((step, i) => {
                      const widthPct = Math.max((step.users / top) * 100, 2);
                      const fromReg = step.from_register != null ? `${(step.from_register * 100).toFixed(1)}%` : '—';
                      const fromPrev = step.from_prev != null ? `${(step.from_prev * 100).toFixed(1)}%` : '—';
                      const tone = i === 0 ? '#3b82f6'
                                 : i === funnelData.funnel.length - 1 ? '#10b981'
                                 : '#6366f1';
                      return (
                        <div key={step.step}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                            <span><strong>{stepLabels[step.step] || step.step}</strong></span>
                            <span>
                              <strong>{step.users.toLocaleString('ja-JP')}</strong>
                              <span className="v2-muted" style={{ marginLeft: 8 }}>
                                from start {fromReg}　/　from prev {fromPrev}
                              </span>
                            </span>
                          </div>
                          <div style={{
                            height: 22,
                            width: `${widthPct}%`,
                            background: `linear-gradient(90deg, ${tone}, ${tone}cc)`,
                            borderRadius: 6,
                            transition: 'width 0.4s ease',
                          }} />
                        </div>
                      );
                    })}
                  </div>
                );
              })()
            )}
          </Card>
          <Card title="新規有料 — 期間内日別" extra={<span className="v2-muted" style={{ fontSize: 12 }}>{dateRange[0].format('M/D')} ~ {dateRange[1].format('M/D')}</span>}>
            {(() => {
              const days = growthRows.filter((r) => r.newPaid > 0);
              const totalNew = days.reduce((s, r) => s + r.newPaid, 0);
              if (days.length === 0) {
                return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="期間内に新規有料なし" />;
              }
              const maxVal = Math.max(...days.map((r) => r.newPaid), 1);
              return (
                <div>
                  <div style={{ marginBottom: 12, fontSize: 13 }}>
                    <Tag color="green">合計 {totalNew} 名</Tag>
                    <span className="v2-muted">（{days.length} 日に発生 / {growthRows.length} 日中）</span>
                  </div>
                  {/* Bar chart - only days with newPaid > 0 */}
                  <div style={{ display: 'flex', alignItems: 'flex-end', height: 140, gap: 8, borderBottom: '1px solid var(--v2-border-soft)', padding: '0 4px 4px' }}>
                    {days.map((r) => {
                      const pct = Math.max((r.newPaid / maxVal) * 100, 20);
                      return (
                        <div
                          key={r.date}
                          title={`${dayjs(r.date).format('M/D (ddd)')}: +${r.newPaid}`}
                          style={{
                            flex: 1,
                            minWidth: 24,
                            maxWidth: 60,
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                          }}
                        >
                          <span style={{ fontSize: 11, fontWeight: 600, color: '#10b981', marginBottom: 2 }}>
                            +{r.newPaid}
                          </span>
                          <div style={{
                            width: '100%',
                            height: `${pct}%`,
                            minHeight: 6,
                            background: 'linear-gradient(180deg, #10b981 0%, #059669 100%)',
                            borderRadius: '4px 4px 0 0',
                            transition: 'height 0.3s ease',
                          }} />
                        </div>
                      );
                    })}
                  </div>
                  {/* Date labels under each bar */}
                  <div style={{ display: 'flex', gap: 8, padding: '4px 4px 0', fontSize: 11, color: 'var(--v2-muted)' }}>
                    {days.map((r) => (
                      <div key={r.date} style={{ flex: 1, minWidth: 24, maxWidth: 60, textAlign: 'center' }}>
                        {dayjs(r.date).format('M/D (ddd)')}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </Card>
        </div>
      ),
    },
    {
      key: 'csv',
      label: 'CSV',
      children: (
        <Card
          title="CSV Export Center"
          extra={<span className="v2-muted" style={{ fontSize: 12 }}>期間: {dateRange[0].format('M/D')} ~ {dateRange[1].format('M/D')}</span>}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {(() => {
              const items: Array<{
                label: string;
                count: number;
                scope: MetricScope;
                onClick: () => void;
                desc: string;
              }> = [
                {
                  label: 'Paid audit',
                  count: effectivePaidUsers.length,
                  scope: '現在',
                  desc: '現在 PRO 全員',
                  onClick: () => toCsv(effectivePaidUsers as Record<string, unknown>[], 'paid_users.csv'),
                },
                {
                  label: 'Feature usage',
                  count: featureUsage.length,
                  scope: '期間内',
                  desc: 'ページ/機能利用集計',
                  onClick: () => toCsv(featureUsage as unknown as Record<string, unknown>[], 'feature_usage.csv'),
                },
                {
                  label: 'Cancellations',
                  count: cancellations.canceled.length,
                  scope: '期間内',
                  desc: '解約者 (Stripe + DB 並集)',
                  onClick: () => toCsv(cancellations.canceled as unknown as Record<string, unknown>[], 'cancellations.csv'),
                },
                {
                  label: 'Pending cancel',
                  count: cancellations.pending_cancel.length,
                  scope: '現在',
                  desc: '即将解約 (cancel_at_period_end=1)',
                  onClick: () => toCsv(cancellations.pending_cancel as unknown as Record<string, unknown>[], 'pending_cancel.csv'),
                },
                {
                  label: 'Retired candidates',
                  count: retiredCandidates.length,
                  scope: '期間内',
                  desc: 'ログ上 退会候補',
                  onClick: () => toCsv(retiredCandidates, 'retired_candidates.csv'),
                },
                {
                  label: 'Risk ranking',
                  count: filteredRiskUsers.length,
                  scope: '期間内',
                  desc: 'リスクランキング (絞り込み後)',
                  onClick: () => toCsv(filteredRiskUsers as unknown as Record<string, unknown>[], 'risk_ranking.csv'),
                },
                {
                  label: 'Alert users',
                  count: alertUsers.length,
                  scope: '現在',
                  desc: '上限アラート (全体)',
                  onClick: () => toCsv(alertUsers as Record<string, unknown>[], 'alert_users.csv'),
                },
              ];
              return items.map((it) => (
                <div key={it.label} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 12px',
                  background: 'var(--v2-surface-soft)',
                  border: '1px solid var(--v2-border-soft)',
                  borderRadius: 6,
                }}>
                  <span
                    className="v2-metric-scope"
                    style={{ ...SCOPE_STYLE[it.scope], position: 'static', flexShrink: 0 }}
                  >
                    {it.scope}
                  </span>
                  <div style={{ flex: 1 }}>
                    <strong>{it.label} CSV</strong>
                    <span className="v2-muted" style={{ marginLeft: 8, fontSize: 12 }}>
                      {it.desc} ({it.count} 件)
                    </span>
                  </div>
                  <Button size="small" icon={<CloudDownloadOutlined />} onClick={it.onClick}>
                    Download
                  </Button>
                </div>
              ));
            })()}
          </div>
        </Card>
      ),
    },
    {
      key: 'events',
      label: '契約イベント',
      children: (
        <div className="v2-tab-grid">
          <div className="v2-metric-grid">
            {metricCard('新規契約', contractEvents.summary?.subscription_started ?? 0, 'subscription_started (Stripe checkout)', 'green', '期間内')}
            {metricCard('更新支払', contractEvents.summary?.subscription_renewed ?? 0, '月次/年次 invoice 支払成功', 'blue', '期間内')}
            {metricCard('解約', contractEvents.summary?.subscription_cancelled ?? 0, 'Stripe subscription.deleted', 'red', '期間内')}
            {metricCard('支払い失敗', contractEvents.summary?.payment_failed ?? 0, 'invoice.payment_failed', 'gold', '期間内')}
          </div>
          <Card
            title="契約イベント Timeline"
            extra={
              <Space size="small">
                <Button size="small" onClick={() => toCsv(contractEvents.events as unknown as Record<string, unknown>[], `contract_events_${dateRange[0].format('YYYYMMDD')}_${dateRange[1].format('YYYYMMDD')}.csv`)}>CSV</Button>
                <span className="v2-muted" style={{ fontSize: 12 }}>{contractEvents.events.length} 件</span>
              </Space>
            }
          >
            {contractEvents.events.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="期間内に契約イベントなし" />
            ) : (
              <Table
                size="small"
                rowKey="pay_id"
                dataSource={contractEvents.events}
                pagination={{ pageSize: 20 }}
                columns={[
                  { title: '時刻', dataIndex: 'time', width: 150 },
                  {
                    title: 'イベント', dataIndex: 'event_type', width: 130,
                    render: (v: ContractEvent['event_type']) => {
                      const colorMap = {
                        subscription_started: { color: 'green', label: '新規契約' },
                        subscription_renewed: { color: 'blue', label: '更新支払' },
                        subscription_cancelled: { color: 'red', label: '解約' },
                        payment_failed: { color: 'gold', label: '支払い失敗' },
                      };
                      const m = colorMap[v] || { color: 'default', label: v };
                      return <Tag color={m.color}>{m.label}</Tag>;
                    },
                    filters: [
                      { text: '新規契約', value: 'subscription_started' },
                      { text: '更新支払', value: 'subscription_renewed' },
                      { text: '解約', value: 'subscription_cancelled' },
                      { text: '支払い失敗', value: 'payment_failed' },
                    ],
                    onFilter: (val, rec) => rec.event_type === val,
                  },
                  {
                    title: 'ユーザ', dataIndex: 'full_name', width: 220,
                    render: (_, rec) => (
                      <div>
                        <strong>{rec.full_name || '-'}</strong>
                        <div className="v2-muted" style={{ fontSize: 11 }}>{rec.email || '-'}</div>
                      </div>
                    ),
                  },
                  {
                    title: '医院', dataIndex: 'clinic_name', width: 200, ellipsis: true,
                    render: (v: string) => v || <span className="v2-muted">-</span>,
                  },
                  { title: '契約', dataIndex: 'product', width: 80, render: (v: string) => <Tag>{v}</Tag> },
                  {
                    title: '金額 (JPY)', dataIndex: 'amount', width: 100, align: 'right' as const,
                    render: (v: number | null) => v != null ? `¥${Number(v).toLocaleString('ja-JP')}` : <span className="v2-muted">-</span>,
                  },
                ]}
                scroll={{ x: 880 }}
              />
            )}
          </Card>
        </div>
      ),
    },
    {
      key: 'compare',
      label: '期間比較',
      children: (
        <div className="v2-tab-grid">
          <Card title="期間 A vs 期間 B 比較">
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <Space wrap size="middle">
                <div>
                  <div className="v2-muted" style={{ fontSize: 12 }}>期間 A (current)</div>
                  <RangePicker value={periodA} onChange={(v) => v?.[0] && v?.[1] && setPeriodA([v[0], v[1]])} />
                </div>
                <div>
                  <div className="v2-muted" style={{ fontSize: 12 }}>期間 B (compare against)</div>
                  <RangePicker value={periodB} onChange={(v) => v?.[0] && v?.[1] && setPeriodB([v[0], v[1]])} />
                </div>
                <div>
                  <div className="v2-muted" style={{ fontSize: 12 }}>セグメント (任意)</div>
                  <Space>
                    <Select
                      value={compareSegment}
                      onChange={(v) => { setCompareSegment(v); setCompareSegmentValue(''); }}
                      style={{ width: 180 }}
                      options={[
                        { value: '', label: '(全体)' },
                        { value: 'prefecture', label: '都道府県' },
                        { value: 'specialty', label: '専門' },
                        { value: 'subscription_source', label: '契約源' },
                        { value: 'vip_status', label: 'プラン' },
                      ]}
                    />
                    {compareSegment && compareResult?.segment_options && (
                      <Select
                        value={compareSegmentValue}
                        onChange={setCompareSegmentValue}
                        style={{ width: 200 }}
                        placeholder="値を選択"
                        options={
                          (compareResult.segment_options as any)[compareSegment]?.map((v: any) => ({
                            value: String(v), label: String(v),
                          })) || []
                        }
                      />
                    )}
                  </Space>
                </div>
                <Button type="primary" loading={compareLoading} onClick={runCompare}>
                  比較する
                </Button>
                {compareResult && (
                  <Button onClick={() => toCsv(
                    compareResult.comparison as unknown as Record<string, unknown>[],
                    `compare_${periodA[0].format('YYYYMMDD')}_${periodA[1].format('YYYYMMDD')}_vs_${periodB[0].format('YYYYMMDD')}_${periodB[1].format('YYYYMMDD')}.csv`
                  )}>CSV</Button>
                )}
              </Space>

              {compareResult && (
                <Table
                  size="small"
                  rowKey="key"
                  pagination={false}
                  dataSource={compareResult.comparison}
                  columns={[
                    { title: 'Metric', dataIndex: 'metric', width: 200 },
                    { title: `Period A (${periodA[0].format('M/D')} - ${periodA[1].format('M/D')})`, dataIndex: 'periodA', align: 'right' as const, render: (v: number) => v.toLocaleString('ja-JP') },
                    { title: `Period B (${periodB[0].format('M/D')} - ${periodB[1].format('M/D')})`, dataIndex: 'periodB', align: 'right' as const, render: (v: number) => v.toLocaleString('ja-JP') },
                    {
                      title: 'Δ (B - A)', dataIndex: 'delta', align: 'right' as const,
                      render: (v: number) => (
                        <span style={{ color: v > 0 ? '#10b981' : v < 0 ? '#ef4444' : 'inherit' }}>
                          {v > 0 ? '+' : ''}{v.toLocaleString('ja-JP')}
                        </span>
                      ),
                    },
                    {
                      title: 'Change %', dataIndex: 'change_pct', align: 'right' as const,
                      render: (v: number | null) => v === null ? <span className="v2-muted">N/A</span> : (
                        <Tag color={v > 0 ? 'green' : v < 0 ? 'red' : 'default'}>
                          {v > 0 ? '+' : ''}{v.toFixed(1)}%
                        </Tag>
                      ),
                    },
                  ]}
                />
              )}
            </Space>
          </Card>
        </div>
      ),
    },
    {
      key: 'friction',
      label: 'ユーザ困惑分析',
      children: (
        <div className="v2-tab-grid">
          <Card title={`Top ${frictionUsers.length} 困惑ユーザ (直近 ${Math.min(dateRange[1].diff(dateRange[0], 'day') + 1, 30)} 日)`}
            extra={frictionLoading ? <Spin size="small" /> : <Button size="small" onClick={() => toCsv(frictionUsers.map(u => ({
              kaiin_code: u.kaiin_code, full_name: u.full_name, email: u.email,
              total_signal_count: u.total_signal_count,
              signals: Object.entries(u.signals).map(([k, v]) => `${k}:${v.count}`).join('|'),
            })) as Record<string, unknown>[], 'user_friction.csv')}>CSV</Button>}>
            <Table
              size="small"
              rowKey="account_id"
              dataSource={frictionUsers}
              pagination={{ pageSize: 20 }}
              expandable={{
                rowExpandable: (rec) => Object.values(rec.signals || {}).some((s) => s.sample_html || s.sample_url),
                expandedRowRender: (rec) => (
                  <div className="v2-friction-sample-grid">
                    {Object.entries(rec.signals)
                      .sort((a, b) => b[1].count - a[1].count)
                      .map(([sigName, sig]) => {
                        const meta = SIGNAL_LABELS[sigName] || { ja: sigName, color: 'default', hint: '' };
                        const isLabeled = !!sig.label?.label;
                        const tabBadge = parseTabIndex(sig.sample_url);
                        return (
                          <div key={sigName} className="v2-friction-sample-row">
                            <div className="v2-friction-sample-head">
                              <Tag color={meta.color}>{meta.ja}</Tag>
                              <span className="v2-muted" style={{ fontSize: 11 }}>×{sig.count}</span>
                              {sig.last_at && <span className="v2-muted" style={{ fontSize: 11 }}>最終: {sig.last_at}</span>}
                              {tabBadge && <Tag color="cyan" style={{ fontSize: 10 }}>tab: {tabBadge}</Tag>}
                              {isLabeled && (
                                <Tag color="purple" style={{ marginLeft: 4 }}>
                                  📌 {sig.label!.label}
                                </Tag>
                              )}
                              {!isLabeled && sig.sample_signature && (
                                <span className="v2-muted" style={{ fontSize: 10, fontFamily: 'monospace' }}>{sig.sample_signature}</span>
                              )}
                              {sig.sample_hash && (
                                <Button size="small" type="link" style={{ padding: 0, fontSize: 11 }} onClick={() => openLabelModal(sig)}>
                                  {isLabeled ? '✏️ 編集' : '🏷 命名する'}
                                </Button>
                              )}
                            </div>
                            {sig.sample_url && (
                              <div className="v2-friction-sample-url" title={sig.sample_url}>
                                URL: <code>{sig.sample_url}</code>
                              </div>
                            )}
                            {!isLabeled && (
                              sig.sample_html ? (
                                <pre className="v2-friction-sample-html">{sig.sample_html}</pre>
                              ) : (
                                <div className="v2-muted" style={{ fontSize: 11, fontStyle: 'italic' }}>
                                  (HTML サンプル無し: business event ベース信号)
                                </div>
                              )
                            )}
                          </div>
                        );
                      })}
                  </div>
                ),
              }}
              columns={[
                {
                  title: '氏名 / Email', dataIndex: 'full_name', width: 220,
                  render: (_, rec) => (
                    <div>
                      <strong>{rec.full_name || '-'}</strong>
                      <div className="v2-muted">{rec.email || '-'}</div>
                    </div>
                  ),
                },
                {
                  title: '契約源 / プラン', dataIndex: 'subscription_source', width: 130,
                  render: (v, rec) => renderContractSource(v, rec.vip_status),
                },
                {
                  title: 'セッション品質',
                  dataIndex: 'dominant_quality',
                  width: 220,
                  render: (dq: string | null, rec) => {
                    const qb = rec.quality_breakdown || {};
                    const total = Object.values(qb).reduce((s: number, v: number) => s + v, 0);
                    const color = dq === 'engaged' ? 'green' : dq === 'browsing' ? 'orange' : dq === 'tab_open' ? 'red' : 'default';
                    const labelMap: Record<string, string> = { engaged: '実利用', browsing: '閲覧のみ', tab_open: 'タブ開放' };
                    return (
                      <div>
                        <Tag color={color}>{labelMap[dq || ''] || 'unknown'}</Tag>
                        {total > 0 && (
                          <div className="v2-muted" style={{ fontSize: 11 }}>
                            {qb.engaged ? `実利用:${qb.engaged} ` : ''}
                            {qb.browsing ? `閲覧:${qb.browsing} ` : ''}
                            {qb.tab_open ? `放置:${qb.tab_open}` : ''}
                          </div>
                        )}
                      </div>
                    );
                  },
                  filters: [
                    { text: 'engaged', value: 'engaged' },
                    { text: 'browsing', value: 'browsing' },
                    { text: 'tab_open', value: 'tab_open' },
                  ],
                  onFilter: (val, rec) => rec.dominant_quality === val,
                },
                {
                  title: '困惑信号',
                  dataIndex: 'signals',
                  width: 480,
                  render: (signals: Record<string, FrictionSignal>) => (
                    <Space wrap>
                      {Object.entries(signals)
                        .sort((a, b) => b[1].count - a[1].count)
                        .map(([sigName, sig]) => {
                          const meta = SIGNAL_LABELS[sigName] || { ja: sigName, color: 'default', hint: '' };
                          const title = sig.label?.label
                            ? `${meta.hint}\n要素: ${sig.label.label}`
                            : meta.hint;
                          return (
                            <Tag key={sigName} color={meta.color} title={title}>
                              {meta.ja}: {sig.count}
                              {sig.label?.label && <span style={{ marginLeft: 4, opacity: 0.85 }}>({sig.label.label})</span>}
                            </Tag>
                          );
                        })}
                    </Space>
                  ),
                },
                {
                  title: '合計',
                  dataIndex: 'total_signal_count',
                  width: 80,
                  align: 'right' as const,
                  sorter: (a, b) => a.total_signal_count - b.total_signal_count,
                  render: (v) => <Badge count={v} overflowCount={999} style={{ backgroundColor: '#ef4444' }} />,
                },
                {
                  title: '最終ログイン', dataIndex: 'last_login_time', width: 130,
                  render: (v) => v ? String(v).split(' ')[0] : <span className="v2-muted">-</span>,
                },
                {
                  title: 'LLM 深掘り',
                  key: 'llm',
                  width: 130,
                  fixed: 'right' as const,
                  render: (_, rec) => (
                    <Button
                      icon={<BulbOutlined />}
                      size="small"
                      type="primary"
                      onClick={() => runLLMAnalysis(rec)}
                    >
                      分析
                    </Button>
                  ),
                },
              ]}
              scroll={{ x: 1300 }}
            />
          </Card>
        </div>
      ),
    },
  ];

  return (
    <Layout className="v2-shell">
      <Sider width={248} className="v2-sidebar">
        <div className="v2-brand">
          <div className="v2-logo">D</div>
          <div>
            <strong>DIPCeph</strong>
            <span>Operations Analytics</span>
          </div>
        </div>
        <div className="v2-sidebar-section">Insights</div>
        {tabItems.slice(0, 8).map((item) => (
          <button className={`v2-nav-item ${activeTab === item.key ? 'active' : ''}`} key={item.key} onClick={() => setActiveTab(item.key)}>
            {item.key === 'overview' && <DashboardOutlined />}
            {item.key === 'paid' && <TeamOutlined />}
            {item.key === 'churn' && <UserSwitchOutlined />}
            {item.key === 'pages' && <BarChartOutlined />}
            {item.key === 'dormant' && <WarningOutlined />}
            {item.key === 'risk' && <LineChartOutlined />}
            {item.key === 'onboard' && <CalendarOutlined />}
            {item.key === 'csv' && <FileTextOutlined />}
            <span>{item.label}</span>
          </button>
        ))}
        <div className="v2-sidebar-section">Build-out</div>
        {tabItems.slice(8).map((item) => (
          <button className={`v2-nav-item ${activeTab === item.key ? 'active' : ''}`} key={item.key} onClick={() => setActiveTab(item.key)}>
            {item.key === 'events' && <UserSwitchOutlined />}
            {item.key === 'compare' && <LineChartOutlined />}
            {item.key === 'friction' && <BulbOutlined />}
            <span>{item.label}</span>
          </button>
        ))}
        <div className="v2-sidebar-note">
          <span>Mode</span>
          <strong>運営インサイト</strong>
        </div>
      </Sider>
      <Layout>
        <Header className="v2-header">
          <div>
            <Text className="v2-kicker">Internal Dashboard</Text>
            <Title level={2}>PRO Activation & Retention</Title>
          </div>
          <Space className="v2-toolbar" wrap>
            <AnalyticsModeSwitch />
            <Button icon={<CloudDownloadOutlined />} onClick={() => toCsv(filteredRiskUsers as unknown as Record<string, unknown>[], `analytics_${dateRange[0].format('YYYYMMDD')}_${dateRange[1].format('YYYYMMDD')}.csv`)}>
              Export CSV
            </Button>
            <Button icon={<SearchOutlined />} onClick={() => setSearchModalVisible(true)}>
              ユーザー検索
            </Button>
            <RangePicker
              value={dateRange}
              format="YYYY-MM-DD"
              onChange={(value) => {
                if (value?.[0] && value?.[1]) setDateRange([value[0], value[1]]);
              }}
              disabledDate={(current) => Boolean(current && availableDates.length && !availableDates.includes(current.format('YYYY-MM-DD')))}
            />
          </Space>
        </Header>
        <Content className="v2-content">
          {loadError && (
            <Alert
              className="v2-error-alert"
              type="error"
              showIcon
              message="Analytics API error"
              description={loadError}
            />
          )}
          <Spin spinning={loading}>
            <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} className="v2-tabs" />
          </Spin>
        </Content>
      </Layout>
      <UserSearchModal visible={searchModalVisible} onClose={() => setSearchModalVisible(false)} />

      {/* task #31: hash 命名 modal */}
      <Modal
        title={labelModal.existingLabel ? '命名を編集' : 'HTML hash に名前を付ける'}
        open={labelModal.open}
        onCancel={() => setLabelModal({ open: false })}
        onOk={saveLabel}
        confirmLoading={labelModal.saving}
        okText="保存"
        cancelText="キャンセル"
        width={620}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <div className="v2-muted" style={{ fontSize: 12, marginBottom: 4 }}>Signature (要素 ID)</div>
            <code style={{ background: 'var(--v2-surface-soft)', padding: '2px 6px', borderRadius: 4, fontSize: 12 }}>
              {labelModal.signature || labelModal.html_hash}
            </code>
            {labelModal.url_path && (
              <span style={{ marginLeft: 12, fontSize: 12 }} className="v2-muted">
                URL: <code style={{ fontSize: 11 }}>{labelModal.url_path}</code>
              </span>
            )}
            {parseTabIndex(labelModal.sample_url) && (
              <Tag color="cyan" style={{ marginLeft: 8, fontSize: 10 }}>tab: {parseTabIndex(labelModal.sample_url)}</Tag>
            )}
          </div>
          {labelModal.sample_url && (
            <div>
              <div className="v2-muted" style={{ fontSize: 12, marginBottom: 4 }}>サンプル URL</div>
              <code style={{ fontSize: 11, wordBreak: 'break-all' }}>{labelModal.sample_url}</code>
            </div>
          )}
          {labelModal.sample_html && (
            <div>
              <div className="v2-muted" style={{ fontSize: 12, marginBottom: 4 }}>サンプル HTML</div>
              <pre className="v2-friction-sample-html" style={{ maxHeight: 140 }}>{labelModal.sample_html}</pre>
            </div>
          )}
          <div>
            <div style={{ fontSize: 12, marginBottom: 4, fontWeight: 500 }}>
              名前 <span style={{ color: 'var(--v2-red)' }}>*</span>
            </div>
            <Input
              placeholder="例: 重ね合わせ画面の「保存」ボタン"
              value={labelModal.label}
              onChange={(e) => setLabelModal((prev) => ({ ...prev, label: e.target.value }))}
              maxLength={200}
              showCount
            />
          </div>
          <div>
            <div className="v2-muted" style={{ fontSize: 12, marginBottom: 4 }}>メモ (任意)</div>
            <Input.TextArea
              placeholder="補足情報"
              value={labelModal.notes}
              onChange={(e) => setLabelModal((prev) => ({ ...prev, notes: e.target.value }))}
              rows={2}
            />
          </div>
        </Space>
      </Modal>

      <Modal
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, paddingRight: 24 }}>
            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {llmModal.user
                ? `LLM 深掘り: ${llmModal.user.full_name || llmModal.user.account_id}${llmModal.user.email ? ` (${llmModal.user.email})` : ''}`
                : 'LLM 深掘り'}
            </span>
            {llmModal.user && llmModal.data && !llmModal.loading && (
              <Button
                size="small"
                onClick={() => llmModal.user && runLLMAnalysis(llmModal.user, true)}
              >
                🔄 再分析
              </Button>
            )}
          </div>
        }
        open={llmModal.visible}
        onCancel={() => setLlmModal({ visible: false, loading: false })}
        width={840}
        footer={null}
      >
        {llmModal.loading && (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <Spin tip="Claude Sonnet が解析中…" />
          </div>
        )}
        {llmModal.error && (
          <Alert type="error" showIcon message="LLM 分析失敗" description={llmModal.error} style={{ marginBottom: 16 }} />
        )}
        {llmModal.data && !llmModal.loading && (
          <div className="v2-tab-grid">
            <Space wrap size="small" align="center">
              <Tag color="blue">events: {llmModal.data.event_count}</Tag>
              <Tag color="blue">clicks: {llmModal.data.click_count}</Tag>
              <Tag>days: {llmModal.data.days}</Tag>
              <Tag color="green">model: {llmModal.data.model}</Tag>
              {llmModal.data.usage && (
                <Tag color="purple">
                  tokens in/out: {llmModal.data.usage.input_tokens}/{llmModal.data.usage.output_tokens}
                  {llmModal.data.usage.cache_read_input_tokens ? ` (cache hit ${llmModal.data.usage.cache_read_input_tokens})` : ''}
                </Tag>
              )}
              {llmModal.data.cached ? (
                <Tag color="geekblue">キャッシュ済 ({llmModal.data.analyzed_at})</Tag>
              ) : (
                llmModal.data.analyzed_at && <Tag color="cyan">新規分析 ({llmModal.data.analyzed_at})</Tag>
              )}
            </Space>

            {llmModal.data.llm_analysis._parse_error ? (
              <Card title="LLM raw output (JSON parse 失敗)">
                <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>{llmModal.data.llm_analysis.raw_text}</pre>
              </Card>
            ) : (
              <>
                <Card title="🎬 ナラティブ要約">
                  <Typography.Paragraph>{llmModal.data.llm_analysis.summary_timeline || '-'}</Typography.Paragraph>
                </Card>
                <Card title="⚠️ 困惑ポイント">
                  {(llmModal.data.llm_analysis.friction_points || []).length === 0 ? (
                    <Empty description="検出なし" />
                  ) : (
                    <Table
                      size="small"
                      pagination={false}
                      dataSource={llmModal.data.llm_analysis.friction_points!.map((p, i) => ({ ...p, key: i }))}
                      columns={[
                        { title: 'Step', dataIndex: 'step', width: 200 },
                        { title: 'Issue', dataIndex: 'issue' },
                        { title: 'Evidence', dataIndex: 'evidence_time', width: 160 },
                      ]}
                    />
                  )}
                </Card>
                <Card title="🔍 離脱原因仮説">
                  <Typography.Paragraph>{llmModal.data.llm_analysis.drop_reason_hypothesis || '-'}</Typography.Paragraph>
                </Card>
                <Card title="💡 UI 改善案">
                  <ul>
                    {(llmModal.data.llm_analysis.ui_improvement || []).map((s, i) => (
                      <li key={i}>{s}</li>
                    ))}
                  </ul>
                </Card>
              </>
            )}
          </div>
        )}
      </Modal>
    </Layout>
  );
};

export default DashboardV2;
